import React, { useState, useEffect, useRef } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useDuration } from 'context/DurationContext';
import { durationApi } from 'api/durationApi';

const DurationDropdown = ({
  value,
  onChange,
  label = "Duration",
  required = false,
  error,
  helperText,
  showActiveOnly = true,
  showDefaultFirst = true,
  fullWidth = true,
  size = 'medium',
  variant = 'outlined',
  disabled = false,
  autoSelectDefault = true,
  isControlled = true, 
  skipAutoSelectForControlled = true,
}) => {
  const { 
    durations, 
    loading, 
    error: durationError, 
    refreshDurations,
    getDefaultDuration 
  } = useDuration();
  
  const [localError, setLocalError] = useState('');
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const initialMount = useRef(true);
  const prevValue = useRef(value);

  useEffect(() => {
    // ✅ FIXED: Track value changes
    if (prevValue.current !== value) {
      // Value changed externally (from parent)
      setHasAutoSelected(false);
      prevValue.current = value;
    }
  }, [value]);

  useEffect(() => {
    // ✅ FIXED: Improved auto-select logic
    if (durations.length === 0) return;
    
    // If it's the initial mount and no value is set
    if (initialMount.current && !value && durations.length > 0) {
      initialMount.current = false;
      
      // Skip auto-select for controlled components if specified
      if (isControlled && skipAutoSelectForControlled) {
        return;
      }
      
      // Auto-select default duration only for new/uncontrolled forms
      if (autoSelectDefault) {
        const defaultDuration = getDefaultDuration();
        if (defaultDuration && !hasAutoSelected) {
          setTimeout(() => {
            onChange && onChange(defaultDuration.id);
            setHasAutoSelected(true);
          }, 0);
        }
      }
      return;
    }
    
    // If value becomes undefined/empty later (form reset)
    if (!value && !hasAutoSelected && autoSelectDefault) {
      const defaultDuration = getDefaultDuration();
      if (defaultDuration) {
        setTimeout(() => {
          onChange && onChange(defaultDuration.id);
          setHasAutoSelected(true);
        }, 0);
      }
    }
  }, [durations, value, autoSelectDefault, isControlled, skipAutoSelectForControlled]);

  const handleRefresh = async () => {
    await refreshDurations();
  };

  const formatDurationLabel = (duration) => {
    if (duration.displayLabel) return duration.displayLabel;
    if (duration.label) return duration.label;
    
    const unitLabel = durationApi.getUnitLabel(duration.unit);
    const plural = duration.value > 1 ? 's' : '';
    
    return `${duration.value} ${unitLabel}${plural}`;
  };

  const handleChange = (e) => {
    const selectedValue = e.target.value;
    onChange && onChange(selectedValue);
    setLocalError('');
    setHasAutoSelected(false); // Reset auto-select flag on manual change
  };

  if (loading && durations.length === 0) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading durations...</Typography>
      </Box>
    );
  }

  const errorToShow = durationError || localError || error;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <FormControl 
          fullWidth={fullWidth} 
          size={size}
          error={!!errorToShow}
          disabled={disabled || durations.length === 0}
          variant={variant}
        >
          <InputLabel>
            {label}
            {required && <span style={{ color: 'red' }}>*</span>}
          </InputLabel>
          
          <Select
            value={value || ''}
            onChange={handleChange}
            label={label}
            renderValue={(selected) => {
              if (!selected) return '';
              const duration = durations.find(d => d.id === selected);
              if (!duration) return '';
              
              return (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography>{formatDurationLabel(duration)}</Typography>
                  {duration.isDefault && (
                    <Chip 
                      label="Default" 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  )}
                </Box>
              );
            }}
          >
            {durations.length === 0 ? (
              <MenuItem disabled>
                No durations available
              </MenuItem>
            ) : (
              durations.map((duration) => (
                <MenuItem key={duration.id} value={duration.id}>
                  <Box display="flex" justifyContent="space-between" width="100%">
                    <Box>
                      <Typography>{formatDurationLabel(duration)}</Typography>
                    </Box>
                    
                    <Box display="flex" alignItems="center" gap={1}>
                      {duration.isDefault && (
                        <Chip 
                          label="Default" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      )}
                      
                      {duration.equivalentDays && (
                        <Tooltip title={`Equivalent to ${duration.equivalentDays} days`}>
                          <Chip 
                            label={`${duration.equivalentDays}d`} 
                            size="small" 
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Tooltip title="Refresh durations">
          <IconButton 
            size="small" 
            onClick={handleRefresh}
            disabled={loading}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {helperText && !errorToShow && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
      
      {errorToShow && (
        <Alert severity="error" size="small" sx={{ mt: 1 }}>
          {errorToShow}
        </Alert>
      )}
    </Box>
  );
};

export default DurationDropdown;

// import React, { useState, useEffect } from 'react';
// import {
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Box,
//   Typography,
//   Chip,
//   CircularProgress,
//   Alert,
//   Tooltip,
//   IconButton
// } from '@mui/material';
// import { Refresh } from '@mui/icons-material';
// import { useDuration } from 'context/DurationContext';
// import { durationApi } from 'api/durationApi';

// const DurationDropdown = ({
//   value,
//   onChange,
//   label = "Duration",
//   required = false,
//   error,
//   helperText,
//   showActiveOnly = true,
//   showDefaultFirst = true,
//   fullWidth = true,
//   size = 'medium',
//   variant = 'outlined',
//   disabled = false,
//   autoSelectDefault = true
// }) => {
//   const { 
//     durations, 
//     loading, 
//     error: durationError, 
//     refreshDurations,
//     getDefaultDuration 
//   } = useDuration();
//   const [localError, setLocalError] = useState('');

//   useEffect(() => {
//     // Auto-select default duration if no value selected
//     if (autoSelectDefault && !value && durations.length > 0) {
//       const defaultDuration = getDefaultDuration();
//       if (defaultDuration) {
//         onChange && onChange(defaultDuration.id);
//       }
//     }
//   }, [durations, value, autoSelectDefault]);

//   const handleRefresh = async () => {
//     await refreshDurations();
//   };

//   const formatDurationLabel = (duration) => {
//     // console.log("Formatting duration label for:", duration);

//     if (duration.displayLabel) return duration.displayLabel;
//     if (duration.label) return duration.label;
    
//     const unitLabel = durationApi.getUnitLabel(duration.unit);
//     const plural = duration.value > 1 ? 's' : '';
    
//     return `${duration.value} ${unitLabel}${plural}`;
//   };

//   const handleChange = (e) => {
//     const selectedValue = e.target.value;
//     onChange && onChange(selectedValue);
//     setLocalError('');
//   };

//   if (loading && durations.length === 0) {
//     return (
//       <Box display="flex" alignItems="center" gap={1}>
//         <CircularProgress size={20} />
//         <Typography variant="body2">Loading durations...</Typography>
//       </Box>
//     );
//   }

//   const errorToShow = durationError || localError || error;

//   return (
//     <Box>
//       <Box display="flex" alignItems="center" gap={1} mb={1}>
//         <FormControl 
//           fullWidth={fullWidth} 
//           size={size}
//           error={!!errorToShow}
//           disabled={disabled || durations.length === 0}
//           variant={variant}
//         >
//           <InputLabel>
//             {label}
//             {required && <span style={{ color: 'red' }}>*</span>}
//           </InputLabel>
          
//           <Select
//             value={value || ''}
//             onChange={handleChange}
//             label={label}
//             renderValue={(selected) => {
//               if (!selected) return '';
//               const duration = durations.find(d => d.id === selected);
//               if (!duration) return '';
              
//               return (
//                 <Box display="flex" alignItems="center" gap={1}>
//                   <Typography>{formatDurationLabel(duration)}</Typography>
//                   {duration.isDefault && (
//                     <Chip 
//                       label="Default" 
//                       size="small" 
//                       color="primary" 
//                       variant="outlined"
//                     />
//                   )}
//                 </Box>
//               );
//             }}
//           >
//             {durations.length === 0 ? (
//               <MenuItem disabled>
//                 No durations available
//               </MenuItem>
//             ) : (
//               durations.map((duration) => (
//                 <MenuItem key={duration.id} value={duration.id}>
//                   <Box display="flex" justifyContent="space-between" width="100%">
//                     <Box>
//                       <Typography>{formatDurationLabel(duration)}</Typography>
//                       {/* {duration.description && (
//                         <Typography variant="caption" color="textSecondary" noWrap>
//                           {duration.description}
//                         </Typography>
//                       )} */}
//                     </Box>
                    
//                     <Box display="flex" alignItems="center" gap={1}>
//                       {duration.isDefault && (
//                         <Chip 
//                           label="Default" 
//                           size="small" 
//                           color="primary" 
//                           variant="outlined"
//                         />
//                       )}
                      
//                       {duration.equivalentDays && (
//                         <Tooltip title={`Equivalent to ${duration.equivalentDays} days`}>
//                           <Chip 
//                             label={`${duration.equivalentDays}d`} 
//                             size="small" 
//                             variant="outlined"
//                           />
//                         </Tooltip>
//                       )}
//                     </Box>
//                   </Box>
//                 </MenuItem>
//               ))
//             )}
//           </Select>
//         </FormControl>

//         <Tooltip title="Refresh durations">
//           <IconButton 
//             size="small" 
//             onClick={handleRefresh}
//             disabled={loading}
//           >
//             <Refresh />
//           </IconButton>
//         </Tooltip>
//       </Box>

//       {helperText && !errorToShow && (
//         <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
//           {helperText}
//         </Typography>
//       )}
      
//       {errorToShow && (
//         <Alert severity="error" size="small" sx={{ mt: 1 }}>
//           {errorToShow}
//         </Alert>
//       )}
//     </Box>
//   );
// };

// export default DurationDropdown;
